import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/mock';
import { Globe, Menu, X, Instagram } from 'lucide-react';

export const Header = () => {
  const { language, toggleLanguage } = useLanguage();
  const t = translations[language];
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigateTo = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/80 backdrop-blur-xl shadow-sm' : 'bg-white/60 backdrop-blur-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <button
              onClick={() => navigateTo('/')}
              className="hover:opacity-80 transition-opacity"
            >
              <img
                loading="lazy" decoding="async" src="https://customer-assets.emergentagent.com/job_pedigree-cats/artifacts/7g6z9ew4_Logo%20Ocicat%20Brasil%2001.png"
                alt="Ocicat Brasil"
                className="h-12 md:h-14 w-auto"
              />
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            <button
              onClick={() => navigateTo('/')}
              className={`text-sm transition-colors ${
                isActive('/') 
                  ? 'text-brown-900 font-medium' 
                  : 'text-gray-700 hover:text-brown-900'
              }`}
            >
              {t.nav.home}
            </button>

            {/* Sobre a Raça com dropdown */}
            <div className="relative group">
              <button
                onClick={() => navigateTo('/about')}
                className={`text-sm transition-colors ${
                  isActive('/about') 
                    ? 'text-brown-900 font-medium' 
                    : 'text-gray-700 hover:text-brown-900'
                }`}
              >
                {t.nav.about}
              </button>
              <div className="absolute left-0 mt-2 w-56 bg-white shadow-lg rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-2">
                  <button onClick={() => navigateTo('/about?tab=history')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    {language === 'pt' ? 'História dos Ocicats' : 'Ocicat History'}
                  </button>
                  <button onClick={() => navigateTo('/about?tab=brazil')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    {language === 'pt' ? 'Ocicats no Brasil' : 'Ocicats in Brazil'}
                  </button>
                  <button onClick={() => navigateTo('/about?tab=characteristics')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    {language === 'pt' ? 'Características' : 'Characteristics'}
                  </button>
                  <button onClick={() => navigateTo('/about?tab=colors')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    {language === 'pt' ? 'Padrões de Cores' : 'Color Patterns'}
                  </button>
                </div>
              </div>
            </div>

            {/* Nossos Gatos com dropdown */}
            <div className="relative group">
              <button
                onClick={() => navigateTo('/cats')}
                className={`text-sm transition-colors ${
                  isActive('/cats') 
                    ? 'text-brown-900 font-medium' 
                    : 'text-gray-700 hover:text-brown-900'
                }`}
              >
                {t.nav.cats}
              </button>
              <div className="absolute left-0 mt-2 w-48 bg-white shadow-lg rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-2">
                  <button onClick={() => navigateTo('/cats?tab=studs')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    {language === 'pt' ? 'Padreadores' : 'Studs'}
                  </button>
                  <button onClick={() => navigateTo('/cats?tab=kittens')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    {language === 'pt' ? 'Filhotes' : 'Kittens'}
                  </button>
                  <button onClick={() => navigateTo('/cats?tab=awards')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    {language === 'pt' ? 'Premiações' : 'Awards'}
                  </button>
                  <button onClick={() => navigateTo('/cats?tab=photos')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    {language === 'pt' ? 'Fotos' : 'Photos'}
                  </button>
                </div>
              </div>
            </div>

            {/* Gatil com dropdown */}
            <div className="relative group">
              <button
                onClick={() => navigateTo('/cattery')}
                className={`text-sm transition-colors ${
                  isActive('/cattery') 
                    ? 'text-brown-900 font-medium' 
                    : 'text-gray-700 hover:text-brown-900'
                }`}
              >
                {t.nav.cattery}
              </button>
              <div className="absolute left-0 mt-2 w-56 bg-white shadow-lg rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-2">
                  <button onClick={() => navigateTo('/cattery')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    {language === 'pt' ? 'Gatil Mostacada' : 'Gatil Mostacada'}
                  </button>
                  <button onClick={() => navigateTo('/cattery?tab=how')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    {language === 'pt' ? 'Como ter um Ocicat' : 'How to have an Ocicat'}
                  </button>
                </div>
              </div>
            </div>

            <button
              onClick={() => navigateTo('/contact')}
              className={`text-sm transition-colors ${
                isActive('/contact') 
                  ? 'text-brown-900 font-medium' 
                  : 'text-gray-700 hover:text-brown-900'
              }`}
            >
              {t.nav.contact}
            </button>
          </nav>

          {/* Language Toggle & Social Links */}
          <div className="flex items-center space-x-4">
            {/* Instagram Link */}
            <a
              href="https://www.instagram.com/ocicat.brasil/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-700 hover:text-brown-900 transition-colors"
              aria-label="Instagram Ocicat Brasil"
            >
              <Instagram size={20} />
            </a>

            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-2 text-sm text-gray-700 hover:text-brown-900 transition-colors"
            >
              <Globe size={18} />
              <span className="hidden sm:inline">{language.toUpperCase()}</span>
            </button>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100">
          <div className="px-4 py-4 space-y-3">
            <button
              onClick={() => navigateTo('/')}
              className={`block w-full text-left px-4 py-2 rounded-lg transition-colors ${
                isActive('/') 
                  ? 'bg-gray-100 text-brown-900 font-medium' 
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {t.nav.home}
            </button>
            <button
              onClick={() => navigateTo('/about')}
              className={`block w-full text-left px-4 py-2 rounded-lg transition-colors ${
                isActive('/about') 
                  ? 'bg-gray-100 text-brown-900 font-medium' 
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {t.nav.about}
            </button>
            <button
              onClick={() => navigateTo('/cats')}
              className={`block w-full text-left px-4 py-2 rounded-lg transition-colors ${
                isActive('/cats') 
                  ? 'bg-gray-100 text-brown-900 font-medium' 
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {t.nav.cats}
            </button>
            <button
              onClick={() => navigateTo('/cattery')}
              className={`block w-full text-left px-4 py-2 rounded-lg transition-colors ${
                isActive('/cattery') 
                  ? 'bg-gray-100 text-brown-900 font-medium' 
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {t.nav.cattery}
            </button>
            <button
              onClick={() => navigateTo('/contact')}
              className={`block w-full text-left px-4 py-2 rounded-lg transition-colors ${
                isActive('/contact') 
                  ? 'bg-gray-100 text-brown-900 font-medium' 
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {t.nav.contact}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
